import { Controller } from '@hotwired/stimulus'

// Chrome/Firefox record webm/opus, Safari (iOS included) only supports mp4/aac
const MIME_CANDIDATES = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4']
const EXTENSIONS = { 'audio/webm': 'webm', 'audio/mp4': 'm4a', 'audio/ogg': 'ogg' }

const formatDuration = (totalSeconds) => {
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = String(totalSeconds % 60).padStart(2, '0')
  return `${minutes}:${seconds}`
}

// Records voice notes in the browser and attaches each one to the form as its own
// hidden file input, so they upload through the regular attachments params.
// Controls live in the page title bar while the clips list sits inside the form.
//
// Connects to data-controller="events--voice-recorder"
export default class extends Controller {
  static targets = ['startButton', 'recording', 'timer', 'error', 'section', 'list', 'itemTemplate']
  static values = {
    inputName: String,
    maxSeconds: { type: Number, default: 300 },
    filenamePrefix: { type: String, default: 'voice-note' },
  }

  connect() {
    if (!navigator.mediaDevices?.getUserMedia || !window.MediaRecorder) {
      this.startButtonTarget.disabled = true
      this.errorTarget.classList.remove('d-none')
    }
  }

  disconnect() {
    this.discard = true
    this.stop()
    this.teardown()
    this.listTarget.querySelectorAll('[data-object-url]').forEach(item => URL.revokeObjectURL(item.dataset.objectUrl))
  }

  async start() {
    this.errorTarget.classList.add('d-none')

    try {
      this.stream = await navigator.mediaDevices.getUserMedia({ audio: true })
    } catch {
      this.errorTarget.classList.remove('d-none')
      return
    }

    const mimeType = MIME_CANDIDATES.find(type => MediaRecorder.isTypeSupported(type))
    this.recorder = new MediaRecorder(this.stream, mimeType ? { mimeType } : undefined)
    this.chunks = []
    this.discard = false
    this.recorder.addEventListener('dataavailable', event => {
      if (event.data.size > 0) this.chunks.push(event.data)
    })
    this.recorder.addEventListener('stop', () => this.finalize())
    this.recorder.start()

    this.startedAt = Date.now()
    this.tick()
    this.timerInterval = setInterval(() => this.tick(), 250)
    this.toggleRecordingUI(true)
  }

  stop() {
    if (this.recorder?.state === 'recording') this.recorder.stop()
  }

  cancel() {
    this.discard = true
    this.stop()
  }

  remove(event) {
    const item = event.currentTarget.closest('[data-object-url]')
    URL.revokeObjectURL(item.dataset.objectUrl)
    item.remove()
    this.sectionTarget.classList.toggle('d-none', !this.listTarget.querySelector('.cmp-voice-note:not(.d-none)'))
  }

  tick() {
    const elapsed = Math.floor((Date.now() - this.startedAt) / 1000)
    this.timerTarget.textContent = `${formatDuration(elapsed)} / ${formatDuration(this.maxSecondsValue)}`
    if (elapsed >= this.maxSecondsValue) this.stop()
  }

  finalize() {
    const { chunks, discard } = this
    const baseType = (this.recorder.mimeType || chunks[0]?.type || 'audio/webm').split(';')[0]

    this.teardown()
    this.toggleRecordingUI(false)
    if (discard || chunks.length === 0) return

    const stamp = new Date().toISOString().slice(0, 19).replace(/[T:]/g, '-')
    const filename = `${this.filenamePrefixValue}-${stamp}.${EXTENSIONS[baseType] || 'webm'}`
    this.appendItem(new File(chunks, filename, { type: baseType }))
  }

  appendItem(file) {
    const item = this.itemTemplateTarget.content.firstElementChild.cloneNode(true)
    const objectUrl = URL.createObjectURL(file)
    item.dataset.objectUrl = objectUrl
    item.querySelector('audio').src = objectUrl

    const input = item.querySelector('input[type=file]')
    const transfer = new DataTransfer()
    transfer.items.add(file)
    input.name = this.inputNameValue
    input.files = transfer.files

    this.listTarget.append(item)
    this.sectionTarget.classList.remove('d-none')
    item.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }

  toggleRecordingUI(recording) {
    this.startButtonTarget.classList.toggle('d-none', recording)
    this.recordingTarget.classList.toggle('d-none', !recording)
  }

  teardown() {
    clearInterval(this.timerInterval)
    this.stream?.getTracks().forEach(track => track.stop())
    this.stream = null
  }
}
